import { APIClient } from '../../helpers/api_helper';
import secureApi from '../../utils/secureApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = new APIClient();

// =========================
// Player Interfaces
// =========================

export interface Player {
  _id: string;
  playerId?: string;
  name: string;
  profilePic: string;
  dob: string;
  ageCategory: string;
  parentName?: string; // Optional for existing players, required for new players
  verificationStatus: 'pending' | 'approved' | 'referred_back';
  createdAt: string;
  documents?: PlayerDocument[];
  amountPaid?: number;
  amountDue?: number;
  qrCode?: string;
  playerIdCard?: string;
  remark?: string; // Computed field - last remark from remarks array
  remarks?: Array<{
    verificationStatus: string;
    remark: string | null;
    date: string;
    by: string;
    _id: string;
  }>;
  teamInfo?: {
    _id: string;
    name: string;
    logo: string;
  };
}

export interface PlayerDocument {
  fileName: string;
  key: string;
  type: 'id-proof' | 'consent-form';
}

export interface ListPlayersResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    players: Player[];
  };
}

export interface AddPlayerRequest {
  name: string;
  profilePic: string;
  dob: string; // YYYY-MM-DD format
  ageCategory: string;
  parentName: string; // Required for new players
  documents?: PlayerDocument[];
}

export interface AddPlayerResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface UpdatePlayerRequest {
  name: string;
  profilePic: string;
  dob: string; // YYYY-MM-DD format
  ageCategory: string;
  parentName: string; // Required for player updates
  documents?: PlayerDocument[];
}

export interface UpdatePlayerResponse {
  success: boolean;
  message: string;
  data?: any;
}

// =========================
// Player Information APIs
// =========================

/**
 * Fetch individual player by ID with complete data including documents
 * @param playerId - The player ID
 * @returns Promise<Player> - Player data with documents
 */
export const getPlayerById = async (playerId: string): Promise<Player> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    // APIClient handles token management automatically
    const response = await api.get(`${API_BASE_URL}/api/v1/club/team/player/find-by-id/${playerId}`, null);
    console.log('Response.data:', response.data);
    console.log('Response.data.data:', response.data?.data);
    
    // Handle the API response structure
    if (response && response.data && response.data.data) {
      const playerData = response.data.data;
      
      // Convert API response to our Player interface
      const player: Player = {
        _id: playerData._id,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        remarks: playerData.remarks || [],
        // Get the last remark from the remarks array
        remark: playerData.remarks && playerData.remarks.length > 0 
          ? playerData.remarks[playerData.remarks.length - 1].remark 
          : undefined,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      };
      
      return player;
    }
    
    // Fallback: try direct response.data structure
    if (response && response.data && response.data._id) {
      const playerData = response.data;
      
      // Convert API response to our Player interface
      const player: Player = {
        _id: playerData._id,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        remarks: playerData.remarks || [],
        // Get the last remark from the remarks array
        remark: playerData.remarks && playerData.remarks.length > 0 
          ? playerData.remarks[playerData.remarks.length - 1].remark 
          : undefined,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      };
      
      return player;
    }
    
    throw new Error('Invalid response structure from individual player API');
  } catch (error) {
    console.error('Failed to fetch individual player:', error);
    throw error;
  }
};

/**
 * Fetch players by team ID
 * @param teamId - The team ID
 * @param tournamentId - Optional tournament ID for tournament-specific player data
 * @returns Promise<Player[]> - Array of players
 */
export const getPlayersByTeamId = async (teamId: string, tournamentId?: string): Promise<Player[]> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    // Build URL with optional tournament parameter
    let url = `${API_BASE_URL}/api/v1/club/team/list-players/${teamId}`;
    if (tournamentId) {
      url += `?tournament=${tournamentId}`;
    }

    // APIClient handles token management automatically
    const response = await api.get(url, null);
    
    // Handle the API response structure
    if (response && response.data && response.data.players) {
      const playersData = response.data.players;
      const players: Player[] = playersData.map((playerData: any) => ({
        _id: playerData._id,
        playerId: playerData.playerId,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      }));
      return players;
    }
    
    // Fallback if response structure is different
    if (response && (response as any).players) {
      const playersData = (response as any).players;
      const players: Player[] = playersData.map((playerData: any) => ({
        _id: playerData._id,
        playerId: playerData.playerId,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      }));
      return players;
    }
    
    throw new Error('Invalid response structure from players API');
  } catch (error) {
    console.error('Failed to fetch players:', error);
    
    // If it's a token-related error, provide more specific guidance
    if (typeof error === 'string' && error.includes('Token')) {
      console.error('Token-related error detected. Please check authentication.');
    }
    
    throw error;
  }
};

/**
 * Add a new player to a team
 * @param teamId - The team ID
 * @param playerData - Player data to create
 * @returns Promise<AddPlayerResponse>
 */
export const addPlayer = async (teamId: string, playerData: AddPlayerRequest): Promise<AddPlayerResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await secureApi.post<any>(
      `/api/v1/club/team/add-player/${teamId}`,
      playerData
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    return {
      success: true,
      message: 'Player added successfully',
      data: responseData
    };
  } catch (error: any) {
    console.error('Failed to add player:', error);
    throw new Error(error.response?.data?.message || 'Failed to add player');
  }
};

/**
 * Update an existing player
 * @param playerId - The player ID
 * @param playerData - Player data to update
 * @returns Promise<UpdatePlayerResponse>
 */
export const updatePlayer = async (playerId: string, playerData: UpdatePlayerRequest): Promise<UpdatePlayerResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await secureApi.post<any>(
      `/api/v1/club/team/update-player-details/${playerId}`,
      playerData
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    return {
      success: true,
      message: 'Player updated successfully',
      data: responseData
    };
  } catch (error: any) {
    console.error('Failed to update player:', error);
    throw new Error(error.response?.data?.message || 'Failed to update player');
  }
};

/**
 * Delete a player using SecureApi for consistent authentication and error handling
 * @param playerId - The player ID
 * @returns Promise<void> - Throws error with specific API message on failure
 */
export const deletePlayer = async (playerId: string): Promise<void> => {
  try {
    // Use secureApi POST method (consistent with other delete operations in the codebase)
    await secureApi.post(`/api/v1/club/team/delete-player/${playerId}`, {});
  } catch (error: any) {
    console.error('Failed to delete player:', error);
    
    // Extract meaningful error message from API response
    let errorMessage = 'Failed to delete player. Please try again.';
    
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.title) {
        errorMessage = error.response.data.title;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Get all players for a club
 * @param clubId - The club ID
 * @param pageNum - Page number for pagination (default: 1)
 * @returns Promise<{players: Player[], totalCount: number}>
 */
export const getPlayersByClubId = async (clubId: string, pageNum: number = 1): Promise<{players: Player[], totalCount: number}> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    // APIClient handles token management automatically
    const response = await api.get(`${API_BASE_URL}/api/v1/club/list-players/${clubId}?pageNum=${pageNum}`, null);
    console.log('Response.data:', response.data);
    console.log('Response.data.data:', response.data?.data);
    
    // Handle the API response structure based on actual Postman response
    if (response && response.data && response.data.paginatedResults) {
      const playersData = response.data.paginatedResults;
      const totalCount = response.data.totalCount || 0;
      const players: Player[] = playersData.map((playerData: any) => ({
        _id: playerData._id,
        playerId: playerData.playerId,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      }));
      return { players, totalCount };
    }
    
    // Fallback: try direct response.data structure
    if (response && response.data && Array.isArray(response.data)) {
      const players: Player[] = response.data.map((playerData: any) => ({
        _id: playerData._id,
        playerId: playerData.playerId,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      }));
      return { players, totalCount: players.length };
    }
    
    throw new Error('Invalid response structure from club players API');
  } catch (error) {
    console.error('Failed to fetch club players:', error);
    throw error;
  }
}; 